import { useCallback, useContext, useRef, useState } from 'react';

import { AppContext } from '../../../../../AppContext';
import { createUuidV4Browser } from '../../../../../clientonly/browserCreateUuidV4';
import type { AgentChatMessageSource, ChatMessage } from '../types';
import { streamAiAgentResponse } from '../utils/streamAiAgentResponse';

const maxSessionIdChars = 512;

export interface UseAiAgentResult {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  messages: ChatMessage[];
  inputValue: string;
  setInputValue: (value: string) => void;
  isLoading: boolean;
  isStreaming: boolean;
  sendMessage: (text: string) => void;
  resendLastMessage: () => void;
}

export const useAiAgent = (): UseAiAgentResult => {
  const { cookieManager } = useContext(AppContext);

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);

  const sessionIdRef = useRef<string | undefined>(undefined);

  if (!sessionIdRef.current) {
    const blizzardClientId = cookieManager.getCookie('blizzard_client_id') ?? '';
    const blizzardWebSessionId = cookieManager.getCookie('blizzard_web_session_id') ?? '';

    if (blizzardClientId && blizzardWebSessionId) {
      const sessionId = `${blizzardClientId}|${blizzardWebSessionId}`;

      // If the session ID is too long, leave sessionIdRef as undefined so that the API will generate a new one
      if (sessionId.length <= maxSessionIdChars) {
        sessionIdRef.current = sessionId;
      }
    }
  }

  const sendMessage = useCallback(
    (message: string) => {
      if (!message.trim() || isLoading || isStreaming) return;

      setMessages(prev => [
        ...prev,
        { id: createUuidV4Browser(), role: 'user', content: message.trim() },
      ]);

      // Clear the input value
      setInputValue('');

      // Set the loading state to true
      setIsLoading(true);
      setIsStreaming(true);

      // Create a new message ID for the agent response so that it can be updated as the message is streamed
      const agentMessageId = createUuidV4Browser();

      // Track if this is the first chunk of the message to avoid updating the message ID multiple times
      let isFirstChunk = true;

      // Accumulate the message as it is streamed from the API
      streamAiAgentResponse({
        message,
        sessionId: sessionIdRef.current,
        onChunk: (accumulatedResponse, responseSources) => {
          // Convert response sources to chat message sources
          const sources: AgentChatMessageSource[] =
            responseSources?.map(source => ({ title: source.title, url: source.url })) ?? [];

          // If this is the first chunk, add the message to the conversation history
          if (isFirstChunk) {
            setMessages(prev => [
              ...prev,
              { id: agentMessageId, role: 'agent', content: accumulatedResponse, sources },
            ]);
            isFirstChunk = false;

            // Turn off loading state after the first chunk is received, as streaming state will
            // remain true until the response is complete
            setIsLoading(false);
          }
          // If this is not the first chunk, update the message in the conversation history
          else {
            setMessages(prev =>
              prev.map(msg =>
                msg.id === agentMessageId
                  ? {
                      id: agentMessageId,
                      role: 'agent' as const,
                      content: accumulatedResponse,
                      sources,
                    }
                  : msg
              )
            );
          }
        },
      })
        .then(result => {
          // If we sent a session ID in the request, we will receive that same session ID back.
          // If we didn't send a session ID, the API will generate a new one which we should store and use for future requests.
          if (result.sessionId) {
            sessionIdRef.current = result.sessionId;
          }
        })
        .catch(error => console.error(error))
        .finally(() => {
          setIsLoading(false);
          setIsStreaming(false);
        });
    },
    [isLoading, isStreaming]
  );

  const resendLastMessage = useCallback(() => {
    const lastUserMessage = [...messages].reverse().find(m => m.role === 'user');

    if (lastUserMessage) {
      sendMessage(lastUserMessage.content);
    }
  }, [messages, sendMessage]);

  return {
    isOpen,
    setIsOpen,
    messages,
    inputValue,
    setInputValue,
    isLoading,
    isStreaming,
    sendMessage,
    resendLastMessage,
  };
};
